
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { DeepAnalysisResult } from '@/types/analysisTypes';
import { downloadCSV } from '@/components/deep-analysis/utils';

// Define excluded categories that should never be displayed (but keep ai_readiness)
const EXCLUDED_CATEGORIES = [
  'quotability',
  'document_structure',
  'entity_recognition',
  'rich_media',
  'schema_markup',
  'semantic_depth'
];

export function usePageAnalysisResults(pageAnalysisResult: DeepAnalysisResult | null, urlInput: string) {
  const [activeTab, setActiveTab] = useState("scores");
  
  useEffect(() => {
    if (pageAnalysisResult) {
      console.log("🚨 usePageAnalysisResults hook - BEFORE processing:", {
        overall_score: pageAnalysisResult.overall_score,
        category_scores: pageAnalysisResult.category_scores,
        ai_optimization_present: 'ai_optimization_score' in (pageAnalysisResult.category_scores || {})
      });
      
      console.log("Full analysis result:", pageAnalysisResult);
      console.log("Category scores (raw):", pageAnalysisResult.category_scores);
      console.log("AI Optimization Score present:", 'ai_optimization_score' in (pageAnalysisResult.category_scores || {}));
      
      // Check if we have actionable fixes
      if (pageAnalysisResult.actionable_fixes && pageAnalysisResult.actionable_fixes.length > 0) {
        console.log(`Found ${pageAnalysisResult.actionable_fixes.length} actionable fixes`);
      }
      
      // Check if we have shareability scores
      if (pageAnalysisResult.shareability_scores) {
        console.log("Shareability scores:", pageAnalysisResult.shareability_scores);
      }
      
      // Check if we have bonus insights
      if (pageAnalysisResult.bonus_section) {
        console.log("Bonus section:", pageAnalysisResult.bonus_section);
      }
      
      // Check if all scores are zero, which might indicate a problem
      const allZeroScores = pageAnalysisResult.overall_score === 0 && 
        Object.values(pageAnalysisResult.category_scores).every(score => {
          if (typeof score === 'object') return score.score === 0;
          return score === 0;
        });
        
      if (allZeroScores) {
        console.warn("All scores are zero, possible analysis issue");
      }
      
      // Check if there's an error
      if (pageAnalysisResult.error) {
        console.error("Analysis error:", pageAnalysisResult.error);
      }
      
      console.log("🚨 usePageAnalysisResults hook - AFTER processing:", {
        overall_score: pageAnalysisResult.overall_score,
        category_scores: pageAnalysisResult.category_scores,
        ai_optimization_present: 'ai_optimization_score' in (pageAnalysisResult.category_scores || {})
      });
    }
  }, [pageAnalysisResult]);
  
  // Export page recommendations to CSV
  const handleExportRecommendations = () => {
    if (!pageAnalysisResult) return;
    
    const actionableFixes = pageAnalysisResult.actionable_fixes || [];
    
    if (actionableFixes.length === 0) {
      toast.error('No actionable fixes available to export');
      return;
    }
    
    const csvContent = [
      ['Current Issue', 'Old Score', 'Suggested Fix', 'Rationale', 'New Score'].join(','),
      ...actionableFixes.map(fix => [
        `"${(fix.current_issue || '').replace(/"/g, '""')}"`,
        fix.old_score || '',
        `"${fix.suggested_fix.replace(/"/g, '""')}"`,
        `"${fix.rationale.replace(/"/g, '""')}"`,
        fix.new_score || ''
      ].join(','))
    ].join('\n');
    
    downloadCSV(csvContent, 'page-recommendations.csv');
    toast.success('Recommendations exported to CSV');
  };

  return {
    activeTab,
    setActiveTab,
    handleExportRecommendations
  };
}
